import React from "react"
import styled from "@emotion/styled"
import breakpoints from "@myvp/shared/src/styles/breakpoints"
import FocusLock from "@myvp/shared/src/components/focus-lock"
import { css } from "@emotion/react"
import Times from "@myvp/shared/src/icons/times"
import IconButton from "@myvp/shared/src/components/buttons/icon-button"
import { KEYS } from "@myvp/shared/src/constants"
import useEventListener from "@myvp/shared/src/hooks/use-event-listener"
import { sleep } from "@myvp/shared/src/functions/sleep"
import useMediaQuery from "@myvp/shared/src/hooks/use-media-query"
import useMergeRefs from "@myvp/shared/src/hooks/use-merge-refs"
import { Medium16Heading } from "@myvp/shared/src/typography"
import { isEmptyObject } from "@myvp/shared/src/functions/is-empty-object"
import { useOnClickOutside } from "@myvp/shared/src/hooks/use-on-click-outside"

const Title = styled.div<{ hasTitle: boolean; closeTitle: boolean }>`
  display: ${(props) => (props.hasTitle ? "flex" : "none")};
  color: ${(props) => props.theme.palette.content.neutral.default};
  font-weight: bold;
  width: 100%;
  align-items: ${(props) => (props.closeTitle ? "center" : "flex-start")};
  justify-content: space-between;
  h2 {
    font-size: 13px;
    line-height: 16px;
    margin: 0;
    padding: 0;
  }
`
const IconContainer = styled.div`
  padding-inline: 4px 28px;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`

export const popupContentPaddingBlock = 24

const Div = styled.div`
  outline: none;
  position: absolute;
  border: 1px solid ${(props) => props.theme.palette.border.neutral.default};
  box-sizing: border-box;
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  background: ${(props) => props.theme.palette.background.neutral.alt};
  padding: ${popupContentPaddingBlock}px;
  ${breakpoints("sm")} {
    padding: 10px;
  }
`
const Popup = ({
  visible,
  close,
  title,
  titleTypography,
  icon,
  children,
  closeTitle,
  clearOnClickOutside = true,
  ignoreOnClickOutside,
  ignoreEscapeKey,
  titlePosition = "left",
  focusOnMount = true,
  enableFocusTrap = true,
  hideClose,
  disableClose,
  anchorEl,
  anchorOrigin = {
    vertical: "bottom",
    horizontal: "right",
  },
  focusRef,
  noAnchorOnMobile,
  buttonElement,
  topOffset = 0,
  contentStyle,
  ref,
  ...props
}: PopupProps & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const isXs = useMediaQuery({
    size: "xs",
    callback: () => null,
    dimension: "width",
  })
  React.useEffect(() => {
    if (visible && inputRef && inputRef.current && focusOnMount) {
      inputRef.current.focus()
    }
  }, [visible, inputRef, focusOnMount])
  const [style, setStyle] = React.useState(props.style ?? {})
  const removeAnchor = noAnchorOnMobile && isXs
  const updateStyle = React.useCallback(async () => {
    if (anchorEl && !removeAnchor && inputRef.current) {
      // We want getBoundingClientRect to be calculated after the popup has painted otherwise the positioning is not correct, sleep(0) does not work here
      await Promise.resolve()
      const r = anchorEl.getBoundingClientRect()
      let top = undefined
      let bottom = undefined
      let right = undefined
      let left = undefined
      if (anchorOrigin?.horizontal === "left") {
        /*
                  [  A  ]  -- { right: 240px }
            [     B     ] -- { offset width: 80px }
            [     B     ]
            [     B     ]
            [     B     ]
        
        Take A's right position, then subtract the popup's offset width
        If the offset with was not subtracted, then it'd look like this:
        
                  [  A  ]  -- { right: 240px }
                        [     B     ] -- { offset width: 80px }
                        [     B     ]
                        [     B     ]
                        [     B     ]
         */
        // Add window.scrollX to account for ipad/iphones misplacing the popup when zoomed in
        left = r.right - inputRef.current.offsetWidth + window.scrollX
      } else if (anchorOrigin?.horizontal === "right") {
        /*
                    [  A  ]  -- { left: 200px
                    [     B     ]
                    [     B     ]
                    [     B     ]
                    [     B     ]
        */
        // Add window.scrollX to account for ipad/iphones misplacing the popup when zoomed in
        left = r.left + window.scrollX
      }
      if (anchorOrigin?.vertical === "top") {
        /*
            [     B     ] -- { offset width: 80px }
            [     B     ]
            [     B     ]
            [     B     ]
                  [  A  ]  -- { top: 120px }
            
        Take A's top position, and subtract the offsetHeight of the popup.
        Without the offset height alignment, it'd look like this:
        
                        [   B[  A  ]]
                        [     B     ]
                        [     B     ]
                        [     B     ]
          A is now under B
         */
        top = r.top - inputRef.current.offsetHeight
      } else if (anchorOrigin?.vertical === "bottom") {
        /*
                  [  A  ]  -- { top: 120px }
            [     B     ] -- { offset width: 80px }
            [     B     ]
            [     B     ]
            [     B     ]
         */
        top = r.bottom
      }
      setStyle({
        position: "fixed",
        bottom,
        top: (top || 0) + (topOffset || 0),
        left,
        right,
      })
    } else {
      setStyle(props.style ?? {})
    }
  }, [
    anchorEl,
    anchorOrigin?.horizontal,
    anchorOrigin?.vertical,
    removeAnchor,
    props.style,
    topOffset,
  ])
  React.useLayoutEffect(() => {
    updateStyle()
  }, [anchorEl, updateStyle])
  useEventListener("resize", updateStyle, { active: !!anchorEl })
  useEventListener("scroll", updateStyle, { active: !!anchorEl })
  const closeWithFocus = React.useCallback(async () => {
    close?.()
    if (focusRef) {
      // Make sure that the popup is now closed.
      await sleep(0)

      if ("focus" in focusRef) {
        // Instead of passing a react ref, we can pass an HTML element.
        // This is helpful when binding the focusRef to event.currentTarget
        focusRef.focus()
      } else if (focusRef.current) {
        focusRef.current.focus()
      }
    }
  }, [close, focusRef])

  const mergedRef = useMergeRefs([inputRef, ref])

  const TitleTypography = (titleTypography ?? Medium16Heading) || React.Fragment

  const clickOutsideProps = useOnClickOutside(
    (e) => {
      if (
        visible &&
        e.target !== anchorEl &&
        e.target !== buttonElement &&
        !anchorEl?.contains(e.target as Node) &&
        !buttonElement?.contains(e.target as Node) &&
        !ignoreOnClickOutside?.current?.contains(e.target as Node)
      ) {
        close?.()
      }
    },
    { active: clearOnClickOutside && visible }
  )

  if (!visible) {
    return null
  }
  const ariaLabelledBy = props["aria-labelledby"] ?? "modal-title"
  return (
    <div {...clickOutsideProps}>
      <FocusLock
        disabled={!enableFocusTrap}
        noFocusGuards
        shards={[
          ...(anchorEl ? [anchorEl] : []),
          ...(buttonElement ? [buttonElement] : []),
        ]}
      >
        <Div
          id={props.focusLockModalId ?? "focus-lock-modal"}
          tabIndex={-1}
          aria-labelledby={ariaLabelledBy}
          {...props}
          style={(() => {
            if (
              !removeAnchor &&
              process.env.NODE_ENV !== "test" &&
              anchorEl &&
              isEmptyObject(style as object)
            ) {
              // Hide the popup until it is anchored
              return {
                visibility: "hidden",
              }
            }
            return style
          })()}
          ref={mergedRef}
          role="dialog"
          aria-modal="true"
          onKeyDown={(e) => {
            if (
              e.key === KEYS.escape &&
              !e.defaultPrevented &&
              !ignoreEscapeKey
            ) {
              closeWithFocus()
            }
          }}
        >
          {icon && <IconContainer>{icon}</IconContainer>}
          <Content style={contentStyle}>
            {title && (
              <Title
                id="title-container"
                hasTitle={!!title}
                closeTitle={Boolean(closeTitle)}
              >
                {titlePosition === "center" && <div style={{ width: 35 }} />}
                <div>
                  {(() => {
                    if (props.deprecatedHeading) {
                      return <h2 id={ariaLabelledBy}>{title}</h2>
                    } else if (typeof title === "string") {
                      return (
                        <TitleTypography id={ariaLabelledBy}>
                          {title}
                        </TitleTypography>
                      )
                    } else {
                      return title
                    }
                  })()}
                  {}
                </div>
                {closeTitle && (
                  <IconButton
                    css={[
                      css`
                        visibility: visible;
                      `,
                      hideClose
                        ? css`
                            visibility: hidden;
                          `
                        : null,
                    ]}
                    icon={Times}
                    variant="tertiary"
                    iconColor="default"
                    height="small"
                    title={closeTitle}
                    onClick={() => {
                      closeWithFocus()
                    }}
                    disabled={disableClose}
                  />
                )}
              </Title>
            )}
            {typeof children === "function"
              ? children({ closeWithFocus })
              : children}
          </Content>
        </Div>
      </FocusLock>
    </div>
  )
}

export interface PopupProps {
  className?: string
  hideClose?: boolean
  disableClose?: boolean
  enableFocusTrap?: boolean
  closeTitle?: string
  title?: string | React.ReactNode
  visible: boolean | undefined
  close?: () => void
  clearOnClickOutside?: boolean
  ignoreOnClickOutside?: React.RefObject<HTMLElement | null>
  ignoreEscapeKey?: boolean
  buttonElement?: HTMLButtonElement | null
  titlePosition?: "left" | "center"
  focusOnMount?: boolean
  icon?: React.ReactNode
  anchorEl?: HTMLElement | null
  anchorOrigin?: {
    vertical?: "bottom" | "top"
    horizontal?: "left" | "right"
  }
  style?: React.CSSProperties
  contentStyle?: React.CSSProperties
  focusRef?: HTMLElement | React.RefObject<HTMLElement | null>
  noAnchorOnMobile?: boolean
  focusLockModalId?: string
  titleTypography?: typeof Medium16Heading | false
  "aria-labelledby"?: string
  topOffset?: number
  onTransitionEnd?: React.TransitionEventHandler<HTMLDivElement>
  children?:
    | React.ReactElement
    | ((params: { closeWithFocus: () => void }) => React.ReactNode)
    | React.ReactNode
  id?: string
  /** @deprecated */
  deprecatedHeading?: boolean
}

export default styled(Popup)``
